// Pure decision logic for the MFA gate. Browser-free so the unit tier covers
// it. The DB is the real enforcer (private.mfa_satisfied() + restrictive
// policies); this decides which screen the client shows on the way there.

export interface MfaRequirements {
  enrollment_required: boolean;
  has_verified_factor: boolean;
  backup_codes_allowed: boolean;
  reprompt_days: number | null;
  remaining_backup_codes: number;
  firm_policy: 'off' | 'all' | null;
}

/** What deriveMfaGate can conclude once it has actually looked. */
export type MfaGateDecision = 'none' | 'challenge' | 'enroll';

/**
 * The gate as the app sees it. `pending` means "a session exists but we have
 * not yet decided" — distinct from `none` ("decided: nothing owed"). Consumers
 * MUST treat `pending` as not-yet-usable: at that moment the session may be
 * aal1 with a verified factor, where the restrictive MFA policies filter every
 * row to zero WITHOUT an error, which reads as "this account has no data".
 */
export type MfaGateState = MfaGateDecision | 'pending';

export interface AmrEntry {
  method: string;
  /** Unix seconds, per the JWT amr claim. */
  timestamp: number;
}

const DAY_SECONDS = 86_400;

/** The amr methods GoTrue uses for a TOTP verification. */
function isTotpMethod(method: string): boolean {
  return method === 'totp' || method === 'mfa/totp';
}

/**
 * Decide what stands between this session and the app shell.
 *
 * Priority:
 * 1. A verified factor demanding elevation (nextLevel aal2, not there yet)
 *    → challenge. This holds even if requirements failed to load — the
 *    database would refuse the session anyway.
 * 2. Enrollment required (firm policy / firm admin, from
 *    get_mfa_requirements) and no factor → enroll.
 * 3. At aal2 with a firm re-prompt window: if the last TOTP verification in
 *    amr is older than the window → challenge again. A missing totp amr
 *    entry counts as fresh — the session was just elevated.
 * 4. Otherwise the gate is open.
 */
export function deriveMfaGate(args: {
  currentLevel: string | null;
  nextLevel: string | null;
  requirements: MfaRequirements | null;
  amr: AmrEntry[] | null;
  nowSeconds: number;
}): MfaGateDecision {
  const { currentLevel, nextLevel, requirements, amr, nowSeconds } = args;

  // The session's currentLevel/nextLevel/amr all come from a CACHED JWT that
  // can be up to an hour stale, and factors change underneath it (backup-code
  // recovery, platform-admin reset, enrolling on another device). Verified
  // 2026-08-02: after an admin deletes a factor, a REFRESHED token is aal1
  // with the totp amr entry gone — but until that refresh the cached token
  // still reads aal2 and still lists the factor. requirements comes from
  // get_mfa_requirements(), read live from the database on every assessment,
  // so it is the authority on whether a factor exists. A null requirements
  // (RPC unavailable) falls back to the session alone, failing toward
  // challenging rather than toward skipping.
  const serverKnowsFactor = requirements === null ? null : requirements.has_verified_factor;
  const serverSaysNoFactor = serverKnowsFactor === false;
  const serverSaysHasFactor = serverKnowsFactor === true;
  const elevated = currentLevel === 'aal2';

  // Elevation owed. `serverSaysHasFactor` covers the mirror-image staleness:
  // a factor enrolled on another device leaves THIS session's nextLevel at
  // aal1, and without it the user would sail past the gate into an app where
  // the restrictive policies hide every row — a silent empty app rather than
  // an honest prompt.
  if (!elevated && !serverSaysNoFactor && (nextLevel === 'aal2' || serverSaysHasFactor)) {
    return 'challenge';
  }

  if (requirements?.enrollment_required && !requirements.has_verified_factor) {
    return 'enroll';
  }

  // Re-prompt. Gated on the same server truth: demanding a code from someone
  // whose factor was just deleted is an unanswerable challenge, and an admin
  // reset also removes their backup codes, so the only escape would be to
  // sign out.
  if (
    elevated &&
    !serverSaysNoFactor &&
    requirements?.reprompt_days != null &&
    requirements.reprompt_days > 0
  ) {
    const totpEntries = (amr ?? []).filter(e => isTotpMethod(e.method));
    if (totpEntries.length > 0) {
      const latest = Math.max(...totpEntries.map(e => e.timestamp));
      const age = nowSeconds - latest;
      // A negative age means the reference clock is behind the server that
      // issued the timestamp; treat it as fresh rather than as an enormous
      // stale value. The caller sources nowSeconds from the token's server
      // issued-at where possible, so a wrong device clock cannot manufacture
      // a challenge the user can never satisfy.
      if (age > 0 && age > requirements.reprompt_days * DAY_SECONDS) {
        return 'challenge';
      }
    }
  }

  return 'none';
}
